import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
    Flex, IconArrowRightOutline, Image,
} from '@zillow/constellation';

import ClaimActions from 'app/shared/actions/ClaimActions';
import PropertiesActions from 'app/shared/actions/PropertiesActions';
import UserActions from 'app/shared/actions/UserActions';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import listingClaimUtils from 'app/shared/utils/listingClaimUtils';
import { ModalSelectors } from 'app/shared/selectors';
import ModalActions from 'app/shared/actions/ModalActions';

import { RMWContext } from 'app/shared/providers/RMWContext';
import WebSocketMessageNameConstants from 'app/shared/constants/WebSocketMessageNameConstants';
import { STEP } from 'app/shared/containers/ListingClaimPage/constants';

import WizardBodyTemplate from 'app/shared/components/WizardBodyTemplate';
import InfoList from 'app/shared/components/InfoList';
import ProgressBar from 'app/shared/components/ProgressBar';
import HomeBrowser from 'app/shared/images/HomeBrowser.svg';
import Trophy from 'app/shared/images/Trophy.svg';
import Notification from 'app/shared/images/Notification.svg';
import Transferred from './components/Transferred';
import NothingToTransfer from './components/NothingToTransfer';

import {
    StyledFadeInHeading, StyledWizardActionFooter, StyledFadeInButton, StyledFadeInText,
} from './StyledComponents';

const { makeAddPropertyModal } = ModalSelectors;
const { updateDisplayAddPropertyModal } = ModalActions;

const propTypes = {
    onGoToPrevStep: PropTypes.func.isRequired,
    onGoToStep: PropTypes.func.isRequired,
    progressStepper: PropTypes.node.isRequired,
};

const STEPS = {
    INIT: 'init',
    TRANSFERRING: 'transferring',
    TRANSFERRED: 'transferred',
};

const STEP_GA_CONFIGS = {
    [STEPS.INIT]: {
        gaLabel: AnalyticsConstants.CLAIM_LISTINGS_LABEL.TRANSFER_INTRO,
    },
    [STEPS.TRANSFERRING]: {
        gaLabel: AnalyticsConstants.CLAIM_LISTINGS_LABEL.TRANSFER_PROCESSING,
    },
    [STEPS.TRANSFERRED]: {
        gaLabel: AnalyticsConstants.CLAIM_LISTINGS_LABEL.TRANSFER_COMPLETE,
    },
    [STEPS.NOTHING_TO_TRANSFER]: {
        gaLabel: AnalyticsConstants.CLAIM_LISTINGS_LABEL.NOTHING_TO_TRANSFER,
    },
};

const delayedTransferMessage = (
    <Flex display="flex" flexDirection="column" alignItems="center">
        <StyledFadeInText fontType="bodySmall" fontColor="red500">
            While we prepare your listings for viewing, you can go to your properties.
        </StyledFadeInText>
    </Flex>
);

const infos = [
    'Manage how your listing appears on Zillow.',
    'Create virtual tours via the Zillow 3D Home Tours app.',
    'Set up online applications and lease signing.',
];

const WizConvertListingInfo = ({
    onGoToPrevStep,
    onGoToStep,
    progressStepper,
}) => {
    const { paths } = useContext(RMWContext);
    const dispatch = useDispatch();
    const [hasError, setHasError] = useState(false);
    const [isTransferring, setIsTransferring] = useState(false);
    const [currentStep, setCurrentStep] = useState(STEPS.INIT);
    const [progressPercentage, setProgressPercentage] = useState(0);
    const [isTransferDelayed, setIsTransferDelayed] = useState(false);
    const [propertyCount, setPropertyCount] = useState(null);
    const totalListingCount = useSelector(state => state.user.summary.totalListingCount);
    const { isOpen: isAddPropertyModalOpen } = useSelector(state => makeAddPropertyModal(state));

    const handleWebSocketMessage = (message) => {
        let data;
        try {
            data = JSON.parse(message.data);
        } catch (error) {
            return;
        }
        if (data.name !== WebSocketMessageNameConstants.USER_FEED_TO_MANUAL_STATUS) {
            return;
        }

        const conversionPercentage = listingClaimUtils.getConversionRate(data.payload.count);
        setProgressPercentage(conversionPercentage);
    };


    useEffect(() => {
        if (currentStep === STEPS.TRANSFERRING) {
            let timeout;

            const interval = setInterval(() => {
                if (!hasError && progressPercentage < 100) {
                    try {
                        window.rentalManagerApiWebSocket.addEventListener('message', (message) => {
                            handleWebSocketMessage(message);
                        });
                        timeout = setTimeout(() => {
                            setIsTransferDelayed(true);
                        }, 10000);
                    } catch (error) {
                        dispatch(PropertiesActions.getListingConversionStatus())
                            .then((response) => {
                                setProgressPercentage(listingClaimUtils.getConversionRate(response.count));
                                timeout = setTimeout(() => {
                                    setIsTransferDelayed(true);
                                }, 10000);
                            }).catch(() => {
                                setIsTransferring(false);
                                setHasError(true);
                            });
                    }
                } else {
                    setIsTransferring(false);
                    setCurrentStep(STEPS.TRANSFERRED);
                }
            }, 1000);

            return () => {
                window.rentalManagerApiWebSocket.removeEventListener('message', handleWebSocketMessage);
                clearTimeout(timeout);
                clearInterval(interval);
            };
        }
        if (currentStep === STEPS.TRANSFERRED) {
            setIsTransferDelayed(false);
        }
    }, [currentStep, progressPercentage]);

    const showAddPropertyModal = () => {
        dispatch(updateDisplayAddPropertyModal({ isOpen: true }));
    };

    const handleSearchByOtherContactMethods = () => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK,
            {
                label: AnalyticsConstants.CLAIM_LISTINGS_LABEL.TRANSFERT_COMPLETE,
                action: 'SearchMoreListings',
            },
        ));
        dispatch(ClaimActions.setShowPropertyLookupModal(true));
        onGoToStep(STEP.FIND_LISTING_RESULTS);
    };

    const handleFindlistings = () => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK,
            {
                label: AnalyticsConstants.CLAIM_LISTINGS_LABEL.NOTHING_TO_TRANSFER,
                action: 'FindListings',
            },
        ));
        dispatch(ClaimActions.setShowPropertyLookupModal(true));
        onGoToStep(STEP.FIND_LISTING_RESULTS);
    };

    const handleViewProperties = () => {
        const analyticsOptions = { label: STEP_GA_CONFIGS[currentStep].gaLabel };
        if (currentStep === STEPS.TRANSFERRED) {
            analyticsOptions.action = 'ClicksGoToProperties';
        }
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK_NEXT,
            analyticsOptions,
        ));
        window.router.transitionTo(`${paths.propertiesPage}?itc=listing_claim_complete`);
    };

    const STEP_CONTENT_CONFIGS = {
        [STEPS.INIT]: {
            title: 'Last step, transfer!',
            subtitle: (
                <span>
                    You’re ready to transfer your listings to Zillow Rental Manager.
                    <br />
                    After transferring, you can:
                </span>
            ),
            nextLabel: 'Next',
            icon: <Image src={HomeBrowser} alt="HomeBrowser" />,
            content: <InfoList infos={infos} />,
        },
        [STEPS.TRANSFERRING]: {
            title: 'Transferring...',
            subtitle: <StyledFadeInText>This will only take a second...</StyledFadeInText>,
            nextLabel: 'Next',
            icon: <Image src={HomeBrowser} alt="HomeBrowser" />,
            content: (
                <>
                    <ProgressBar
                        marginTop="sm"
                        marginBottom="xs"
                        value={progressPercentage}
                    />
                    <InfoList infos={infos} />
                    {isTransferDelayed && (
                        <StyledFadeInButton
                            buttonType="primary"
                            onClick={handleViewProperties}
                        >
                            View properties
                            &nbsp;
                            <IconArrowRightOutline />
                        </StyledFadeInButton>
                    )}
                </>),
        },
        [STEPS.TRANSFERRED]: {
            title: <StyledFadeInHeading level={3}><b>Transfer completed!</b></StyledFadeInHeading>,
            icon: <Image src={Trophy} alt="Trophy" />,
            content: <Transferred
                onSearchByOtherContactMethodsClick={handleSearchByOtherContactMethods}
                onViewPropertiesClick={handleViewProperties}
            />,
        },
        [STEPS.NOTHING_TO_TRANSFER]: {
            title: 'There are no listings to transfer',
            nextLabel: 'View properties',
            icon: <Image src={Notification} alt="Notification" />,
            content: <NothingToTransfer
                isAddPropertyModalOpen={isAddPropertyModalOpen}
                onFindListingsClick={handleFindlistings}
            />,
        },
    };

    const handleGoToNextStep = () => {
        const analyticsOptions = { label: STEP_GA_CONFIGS[currentStep].gaLabel };

        if (currentStep === STEPS.NOTHING_TO_TRANSFER) {
            if (propertyCount) {
                analyticsOptions.action = 'ClicksGoToProperties';
                dispatch(analyticsUtils.track.GAEventV2(
                    AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK_NEXT,
                    analyticsOptions,
                ));
                window.router.transitionTo(`${paths.propertiesPage}?itc=listing_claim_complete`);
            } else {
                analyticsOptions.action = 'ListWithZillow';
                dispatch(analyticsUtils.track.GAEventV2(
                    AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK_NEXT,
                    analyticsOptions,
                ));
                showAddPropertyModal();
            }
        }
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK_NEXT,
            analyticsOptions,
        ));
        if (currentStep === STEPS.INIT) {
            setIsTransferring(true);
            dispatch(ClaimActions.convertAllProperties())
                .then(() => {
                    setCurrentStep(STEPS.TRANSFERRING);
                })
                .catch(() => {
                    const includeSummary = true;
                    dispatch(UserActions.getUser({ includeSummary }))
                        .then(() => {
                            setPropertyCount(totalListingCount);
                            setCurrentStep(STEPS.NOTHING_TO_TRANSFER);
                            setIsTransferring(false);
                        }).catch(() => {
                            setHasError(true);
                        });
                });
        }
        if (currentStep === STEPS.TRANSFERRING) {
            setCurrentStep(STEPS.TRANSFERRED);
        }
    };

    const handleGoToPrevStep = () => {
        dispatch(analyticsUtils.track.GAEventV2(
            AnalyticsConstants.GA_EVENTS.CLAIM_LISTINGS_CLICK_BACK,
            { label: STEP_GA_CONFIGS[currentStep].gaLabel },
        ));
        onGoToPrevStep();
    };

    return (
        <WizardBodyTemplate
            title={STEP_CONTENT_CONFIGS[currentStep].title}
            subtitle={isTransferDelayed ? delayedTransferMessage : STEP_CONTENT_CONFIGS[currentStep].subtitle}
            hasError={hasError}
            icon={STEP_CONTENT_CONFIGS[currentStep].icon}
            progressStepper={progressStepper}
            footer={(
                <StyledWizardActionFooter
                    onBackClick={handleGoToPrevStep}
                    onNextClick={handleGoToNextStep}
                    nextLabel={STEP_CONTENT_CONFIGS[currentStep].nextLabel}
                    isBackDisabled={false}
                    isNextDisabled={isTransferring}
                    hideBackButton={currentStep === STEPS.TRANSFERRED}
                    hideNextButton={isTransferDelayed || currentStep === STEPS.TRANSFERRED}
                />
            )}
        >
            {STEP_CONTENT_CONFIGS[currentStep].content}
        </WizardBodyTemplate>
    );
};

WizConvertListingInfo.propTypes = propTypes;

export default WizConvertListingInfo;
